import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, RefreshCw, Settings } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import shopifyAPI from '@/api/shopify';

/**
 * Componente per mostrare stato OAuth Shopify nell'header.
 * 
 * Features:
 * - Verifica stato OAuth all'avvio
 * - Mostra badge colorato in base allo stato
 * - Link a pagina connessione se disconnesso
 * - Pulsante per rivalidare manualmente
 */
export default function ShopifyTokenStatus() {
  const [tokenStatus, setTokenStatus] = useState({
    status: null,
    loading: true,
    error: null,
    shopName: null,
    lastChecked: null,
  });

  const checkToken = async () => {
    setTokenStatus(prev => ({ ...prev, loading: true }));
    
    try {
      // Usa nuovo endpoint OAuth status
      const result = await shopifyAPI.oauthStatus();
      
      setTokenStatus({
        status: result.status, // 'connected', 'disconnected', 'error'
        loading: false,
        error: result.error || null,
        shopName: result.shop_name || null,
        lastChecked: new Date(),
      });
    } catch (error) {
      console.error('Error checking Shopify OAuth status:', error);
      setTokenStatus({
        status: 'error',
        loading: false,
        error: error.message || 'Errore di connessione',
        shopName: null,
        lastChecked: new Date(),
      });
    }
  };

  useEffect(() => {
    checkToken();
    
    // Ricontrolla ogni 30 minuti
    const interval = setInterval(checkToken, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const { status, loading, shopName } = tokenStatus;

  if (loading && !status) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md text-sm">
        <RefreshCw className="h-4 w-4 animate-spin text-gray-500" />
        <span className="text-gray-600">Verifica Shopify...</span>
      </div>
    );
  }

  if (status === 'disconnected' || status === 'error') {
    return (
      <div className="flex items-center gap-2">
        <Alert variant="destructive" className="py-2 px-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2 text-sm">
            {status === 'disconnected' ? 'Shopify non connesso' : 'Errore connessione Shopify'}
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <Link to="/integrazioni/shopify/connetti">
            <Settings className="h-4 w-4 mr-2" />
            Connetti
          </Link>
        </Button>
      </div>
    );
  }

  if (status === 'connected') {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span className="text-sm text-green-700">
            Shopify: {shopName}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={checkToken}
          disabled={loading}
          className="h-8 w-8 p-0"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="h-8 w-8 p-0"
        >
          <Link to="/integrazioni/shopify/connetti">
            <Settings className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  }

  return null;
}
